import { create } from 'zustand';
import {
  type EspecieListItem,
  type EspecieRequest,
  type PresentacionListItem,
  type PresentacionRequest,
  type CalibreListItem,
  type CalibreRequest
} from '../types/especie';
import { EspecieService } from '../services/especieService';

interface EspecieStore {
  // Especies state
  especies: EspecieListItem[];
  especiesLoading: boolean;
  especiesError: string | null;

  // Presentaciones state
  presentaciones: PresentacionListItem[];
  presentacionesLoading: boolean;
  presentacionesError: string | null;

  // Calibres state
  calibres: CalibreListItem[];
  calibresLoading: boolean;
  calibresError: string | null;

  // Especies actions
  fetchEspecies: () => Promise<void>;
  saveEspecie: (especie: EspecieRequest) => Promise<void>;
  clearEspeciesError: () => void;

  // Presentaciones actions
  fetchPresentaciones: () => Promise<void>;
  fetchPresentacionesByEspecie: (idEspecie: number) => Promise<void>;
  savePresentacion: (presentacion: PresentacionRequest) => Promise<void>;
  clearPresentacionesError: () => void;

  // Calibres actions
  fetchCalibres: () => Promise<void>;
  fetchCalibresByPresentacion: (idPresentacion: number) => Promise<void>;
  saveCalibre: (calibre: CalibreRequest) => Promise<void>;
  clearCalibresError: () => void;
}

export const useEspecieStore = create<EspecieStore>((set, get) => ({
  // Initial state
  especies: [],
  especiesLoading: false,
  especiesError: null,

  presentaciones: [],
  presentacionesLoading: false,
  presentacionesError: null,

  calibres: [],
  calibresLoading: false,
  calibresError: null,

  // Especies actions
  fetchEspecies: async () => {
    set({ especiesLoading: true, especiesError: null });
    try {
      const especies = await EspecieService.getEspecies();
      set({ especies, especiesLoading: false });
    } catch (error) {
      set({
        especiesError: error instanceof Error ? error.message : 'Error fetching especies',
        especiesLoading: false,
      });
    }
  },

  saveEspecie: async (especie: EspecieRequest) => {
    set({ especiesLoading: true, especiesError: null });
    try {
      await EspecieService.saveEspecie(especie);
      // Refresh the list after saving
      await get().fetchEspecies();
    } catch (error) {
      set({
        especiesError: error instanceof Error ? error.message : 'Error saving especie',
        especiesLoading: false,
      });
      throw error;
    }
  },

  clearEspeciesError: () => set({ especiesError: null }),

  // Presentaciones actions
  fetchPresentaciones: async () => {
    set({ presentacionesLoading: true, presentacionesError: null });
    try {
      const presentaciones = await EspecieService.getPresentaciones();
      set({ presentaciones, presentacionesLoading: false });
    } catch (error) {
      set({
        presentacionesError: error instanceof Error ? error.message : 'Error fetching presentaciones',
        presentacionesLoading: false,
      });
    }
  },

  fetchPresentacionesByEspecie: async (idEspecie: number) => {
    set({ presentacionesLoading: true, presentacionesError: null });
    try {
      const presentaciones = await EspecieService.getPresentacionesByEspecie(idEspecie);
      set({ presentaciones, presentacionesLoading: false });
    } catch (error) {
      set({
        presentacionesError: error instanceof Error ? error.message : 'Error fetching presentaciones',
        presentacionesLoading: false,
      });
    }
  },

  savePresentacion: async (presentacion: PresentacionRequest) => {
    set({ presentacionesLoading: true, presentacionesError: null });
    try {
      await EspecieService.savePresentacion(presentacion);
      // Refresh the list after saving
      if (presentacion.idEspecie) {
        await get().fetchPresentacionesByEspecie(presentacion.idEspecie);
      }
    } catch (error) {
      set({
        presentacionesError: error instanceof Error ? error.message : 'Error saving presentacion',
        presentacionesLoading: false,
      });
      throw error;
    }
  },

  clearPresentacionesError: () => set({ presentacionesError: null }),

  // Calibres actions
  fetchCalibres: async () => {
    set({ calibresLoading: true, calibresError: null });
    try {
      const calibres = await EspecieService.getCalibres();
      set({ calibres, calibresLoading: false });
    } catch (error) {
      set({
        calibresError: error instanceof Error ? error.message : 'Error fetching calibres',
        calibresLoading: false,
      });
    }
  },

  fetchCalibresByPresentacion: async (idPresentacion: number) => {
    set({ calibresLoading: true, calibresError: null });
    try {
      const calibres = await EspecieService.getCalibresByPresentacion(idPresentacion);
      set({ calibres, calibresLoading: false });
    } catch (error) {
      set({
        calibresError: error instanceof Error ? error.message : 'Error fetching calibres',
        calibresLoading: false,
      });
    }
  },

  saveCalibre: async (calibre: CalibreRequest) => {
    set({ calibresLoading: true, calibresError: null });
    try {
      await EspecieService.saveCalibre(calibre);
      // Refresh the list after saving
      if (calibre.idPresentacion) {
        await get().fetchCalibresByPresentacion(calibre.idPresentacion);
      }
    } catch (error) {
      set({
        calibresError: error instanceof Error ? error.message : 'Error saving calibre',
        calibresLoading: false,
      });
      throw error;
    }
  },

  clearCalibresError: () => set({ calibresError: null }),
}));